import {
  Alert,
  Checkbox,
  CustomTooltip,
  Input,
  StyledReactSelect,
} from '@fountain/fountain-ui-components';
import {
  ButtonBase,
  IconButton,
  InputBase,
  Typography,
} from '@material-ui/core';
import {
  DragAndDropOutlineIcon,
  Icon,
  LockClosedOutlineIcon,
  PencilOutlineIcon,
  SearchOutlineIcon,
  TrashOutlineIcon,
} from '@fountain/ripple/icon';
import { StandardQuestionTemplate } from 'api-clients/monolith';
import cx from 'classnames';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';

import globalMessages from 'shared/global/messages';

import { messages } from './messages';
import { OptionsEditor } from './OptionsEditor';
import { StandardKeyPicker } from './StandardKeyPicker';
import { useStyles } from './styles';
import {
  CUSTOM_TYPES,
  CustomType,
  EditableQuestion,
  isOptionable,
  QuestionErrors,
  QuestionOption,
  templateOptions,
} from './types';

export interface QuestionCardProps {
  question: EditableQuestion;
  errors?: QuestionErrors;
  templates: StandardQuestionTemplate[];
  usedKeys: string[];
  canRestrict: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  onChange: (patch: Partial<EditableQuestion>) => void;
  onRemove: () => void;
}

const TYPE_MESSAGES: Record<CustomType, keyof typeof messages> = {
  text_field: 'typeTextField',
  text_area: 'typeTextArea',
  radio: 'typeRadio',
  checkboxes: 'typeCheckboxes',
  dropdown: 'typeDropdown',
  datepicker: 'typeDatepicker',
  file: 'typeFile',
  address: 'typeAddress',
};

/** The fields a standard key overwrites, which undo puts back. */
type Binding = Pick<
  EditableQuestion,
  | 'key'
  | 'type'
  | 'predefined'
  | 'question'
  | 'options'
  | 'option_bank_id'
  | 'restricted'
  | 'secure'
>;

type UndoableBinding = {
  previous: Binding;
  previousCustomKeyMode: boolean;
  /** The wording the key left in the field; undo keeps any later edit. */
  seededQuestion: string;
  title: string;
};

/** File questions are always restricted; the key carries the flag on the server. */
const ALWAYS_RESTRICTED = ['file', 'avatar', 'resume'];

export const QuestionCard: FC<QuestionCardProps> = ({
  question,
  errors,
  templates,
  usedKeys,
  canRestrict,
  dragHandleProps,
  onChange,
  onRemove,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [customKeyMode, setCustomKeyMode] = useState(
    !question.predefined && Boolean(question.key),
  );
  const [showHint, setShowHint] = useState(Boolean(question.hint));
  const [undoable, setUndoable] = useState<UndoableBinding | null>(null);
  // Undo unmounts the button that holds focus. The key control it restores
  // exists only after the next render, so focus moves from an effect.
  const [refocusKey, setRefocusKey] = useState(false);
  const keyButtonRef = useRef<HTMLButtonElement>(null);
  const keyInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!refocusKey) return;
    (customKeyMode ? keyInputRef : keyButtonRef).current?.focus();
    setRefocusKey(false);
  }, [refocusKey, customKeyMode]);

  const template = question.predefined
    ? templates.find(t => t.key === question.key)
    : undefined;

  const typeLabel = (type: string) => {
    const messageKey = TYPE_MESSAGES[type as CustomType];
    return messageKey
      ? intl.formatMessage(messages[messageKey])
      : intl.formatMessage(messages.typeOther);
  };

  const typeOptions = CUSTOM_TYPES.map(type => ({
    value: type,
    label: typeLabel(type),
  }));
  const currentType = { value: question.type, label: typeLabel(question.type) };

  // The canonical text seeds the field so the recruiter edits a real question
  // rather than a blank line. Wording they typed themselves always survives;
  // only an empty field or the previous standard question's own canonical text
  // is replaced, so switching keys never strands the wrong default.
  const seededQuestion = (chosen: StandardQuestionTemplate) => {
    const current = question.question.trim();
    const untouched = current === '' || current === template?.question.trim();
    return untouched ? chosen.question : question.question;
  };

  const applyTemplate = (chosen: StandardQuestionTemplate) => {
    setPickerAnchor(null);
    if (question.predefined && question.key === chosen.key) return;

    const nextQuestion = seededQuestion(chosen);
    setUndoable({
      previous: {
        key: question.key,
        type: question.type,
        predefined: question.predefined,
        question: question.question,
        options: question.options,
        option_bank_id: question.option_bank_id,
        restricted: question.restricted,
        secure: question.secure,
      },
      previousCustomKeyMode: customKeyMode,
      seededQuestion: nextQuestion,
      title: chosen.title,
    });
    onChange({
      key: chosen.key,
      type: chosen.type,
      predefined: true,
      question: nextQuestion,
      options: null,
      option_bank_id: null,
      restricted: null,
      secure: chosen.secure ?? false,
    });
    setCustomKeyMode(false);
  };

  const undoBinding = () => {
    if (!undoable) return;
    const {
      previous,
      previousCustomKeyMode,
      seededQuestion: seeded,
    } = undoable;
    onChange({
      ...previous,
      question:
        question.question === seeded ? previous.question : question.question,
    });
    setCustomKeyMode(previousCustomKeyMode);
    setUndoable(null);
    setRefocusKey(true);
  };

  const startCustomKey = () => {
    setUndoable(null);
    onChange({
      key: question.predefined ? '' : question.key,
      predefined: false,
      secure: false,
      options: isOptionable(question.type) ? (question.options ?? []) : null,
    });
    setCustomKeyMode(true);
    setPickerAnchor(null);
  };

  const onTypeChange = (option: { value: CustomType }) => {
    onChange({
      type: option.value,
      options: isOptionable(option.value) ? (question.options ?? []) : null,
      restricted: ALWAYS_RESTRICTED.includes(option.value)
        ? true
        : question.restricted,
    });
  };

  const options: QuestionOption[] = question.predefined
    ? templateOptions(template)
    : (question.options ?? []);

  const keyLabel = question.key || intl.formatMessage(messages.enterDataKey);
  const restrictedLocked = ALWAYS_RESTRICTED.includes(question.type);

  return (
    <div
      className={cx(styles.card, { [styles.cardError]: Boolean(errors) })}
      data-testid={`question-card-${question.uid}`}
    >
      <div
        className={styles.dragHandle}
        {...dragHandleProps}
        aria-label={intl.formatMessage(messages.dragQuestion)}
      >
        <Icon icon={DragAndDropOutlineIcon} size="xs" color="inherit" />
      </div>
      <div className={styles.cardBody}>
        <InputBase
          fullWidth
          className={cx(styles.questionInput, {
            [styles.questionInputError]: Boolean(errors?.question),
          })}
          placeholder={intl.formatMessage(messages.questionPlaceholder)}
          value={question.question}
          inputProps={{
            'aria-label': intl.formatMessage(globalMessages.question),
          }}
          onChange={event => onChange({ question: event.target.value })}
        />
        {errors?.question && (
          <Typography variant="caption" className={styles.helperError}>
            {errors.question}
          </Typography>
        )}
        {showHint ? (
          <InputBase
            fullWidth
            className={styles.hintInput}
            placeholder={intl.formatMessage(messages.hintPlaceholder)}
            value={question.hint ?? ''}
            inputProps={{ 'aria-label': intl.formatMessage(messages.hint) }}
            onChange={event => onChange({ hint: event.target.value })}
          />
        ) : (
          <div>
            <ButtonBase
              className={styles.addHint}
              onClick={() => setShowHint(true)}
              disableRipple
            >
              <Typography variant="body2" color="primary">
                {intl.formatMessage(messages.addHint)}
              </Typography>
            </ButtonBase>
          </div>
        )}

        <div className={styles.controlsRow}>
          <div className={styles.customKeyRow}>
            {customKeyMode ? (
              <Input
                size="small"
                rootClass={styles.keyInput}
                value={question.key}
                error={Boolean(errors?.key)}
                hideErrorMessage
                placeholder={intl.formatMessage(messages.customKeyPlaceholder)}
                inputRef={keyInputRef}
                inputProps={{
                  'aria-label': intl.formatMessage(messages.dataKey),
                  className: styles.keyMono,
                }}
                onChange={event => onChange({ key: event.target.value })}
              />
            ) : (
              <button
                ref={keyButtonRef}
                type="button"
                className={cx(styles.keyButton, {
                  [styles.keyError]: Boolean(errors?.key),
                })}
                aria-haspopup="dialog"
                onClick={event => setPickerAnchor(event.currentTarget)}
              >
                {Boolean(question.key) && (
                  <Icon
                    icon={SearchOutlineIcon}
                    size="sm"
                    color="inherit"
                    className={styles.keyIcon}
                  />
                )}
                <span className={styles.keyMono}>{keyLabel}</span>
                {Boolean(question.key) && (
                  <Icon
                    icon={PencilOutlineIcon}
                    size="sm"
                    color="inherit"
                    className={styles.keyPencil}
                  />
                )}
              </button>
            )}
            {question.predefined && (
              <span className={styles.standardChip}>
                {intl.formatMessage(messages.standardKeyChip)}
              </span>
            )}
            {customKeyMode && (
              <ButtonBase
                onClick={event => setPickerAnchor(event.currentTarget)}
                disableRipple
              >
                <Typography variant="body2" color="primary">
                  {intl.formatMessage(messages.standardKeyBadge)}
                </Typography>
              </ButtonBase>
            )}
            {errors?.key && (
              <Typography variant="caption" className={styles.helperError}>
                {errors.key}
              </Typography>
            )}
          </div>

          <div className={styles.controlsRight}>
            <CustomTooltip
              dense
              title={
                question.predefined
                  ? intl.formatMessage(messages.answerTypeLocked)
                  : ''
              }
            >
              <div className={styles.typeSelect}>
                <StyledReactSelect
                  size="small"
                  aria-label={intl.formatMessage(messages.answerType)}
                  options={typeOptions}
                  value={currentType}
                  isDisabled={question.predefined}
                  isSearchable={false}
                  onChange={onTypeChange}
                />
              </div>
            </CustomTooltip>
            <CustomTooltip
              dense
              title={intl.formatMessage(globalMessages.required)}
            >
              <span>
                <Checkbox
                  checked={Boolean(question.required)}
                  inputProps={{
                    'aria-label': intl.formatMessage(globalMessages.required),
                  }}
                  onChange={() => onChange({ required: !question.required })}
                />
              </span>
            </CustomTooltip>
            {question.predefined && question.secure && (
              <CustomTooltip
                dense
                title={intl.formatMessage(messages.secureDescription)}
              >
                <Icon
                  icon={LockClosedOutlineIcon}
                  size="md"
                  color="inherit"
                  role="img"
                  className={styles.iconControl}
                  aria-label={intl.formatMessage(messages.secureDescription)}
                />
              </CustomTooltip>
            )}
            {!question.predefined && canRestrict && (
              <CustomTooltip
                dense
                title={intl.formatMessage(messages.restrictedDescription)}
              >
                <span>
                  <IconButton
                    size="small"
                    className={styles.restrictedButton}
                    aria-label={intl.formatMessage(messages.restricted)}
                    aria-pressed={
                      Boolean(question.restricted) || restrictedLocked
                    }
                    disabled={restrictedLocked}
                    onClick={() =>
                      onChange({ restricted: !question.restricted })
                    }
                  >
                    <Icon
                      icon={LockClosedOutlineIcon}
                      size="md"
                      color="inherit"
                      className={cx(styles.iconControl, {
                        [styles.iconControlOn]:
                          Boolean(question.restricted) || restrictedLocked,
                      })}
                    />
                  </IconButton>
                </span>
              </CustomTooltip>
            )}
            <IconButton
              size="small"
              className={styles.removeButton}
              aria-label={intl.formatMessage(messages.removeQuestion)}
              onClick={onRemove}
            >
              <Icon icon={TrashOutlineIcon} size="xs" color="inherit" />
            </IconButton>
          </div>
        </div>

        {/* Mounted before its text: a live region that arrives with its
            content is often not announced. */}
        <span role="status" className={styles.visuallyHidden}>
          {undoable &&
            intl.formatMessage(messages.standardKeyApplied, {
              title: undoable.title,
            })}
        </span>
        {undoable && (
          <Alert type="informational" fullWidth>
            <span className={styles.undoBar}>
              <span aria-hidden="true">
                {intl.formatMessage(messages.standardKeyApplied, {
                  title: undoable.title,
                })}
              </span>
              <ButtonBase onClick={undoBinding} disableRipple>
                <Typography variant="body2" color="primary" component="span">
                  {intl.formatMessage(messages.undoStandardKey)}
                </Typography>
              </ButtonBase>
            </span>
          </Alert>
        )}

        {isOptionable(question.type) && (
          <OptionsEditor
            options={options}
            readOnly={Boolean(question.predefined)}
            error={errors?.options}
            onChange={next => onChange({ options: next })}
          />
        )}
      </div>

      <StandardKeyPicker
        anchorEl={pickerAnchor}
        templates={templates}
        usedKeys={usedKeys.filter(key => key !== question.key)}
        onSelect={applyTemplate}
        onCreateCustomKey={startCustomKey}
        onClose={() => setPickerAnchor(null)}
      />
    </div>
  );
};
